import axios from "axios";
import {
  API_GET_DEFAULT_ROLE_BY_NAME,
  API_GET_ROLE_BY_NAME,
} from "../Util/URLConstants";

const normalizeRoleName = (name) => String(name || "").trim().toLowerCase();

export const extractRolePermissionIds = (role) =>
  (role?.permissions || []).map((permission) => permission.id).filter(Boolean);

/** Prefer the current user's saved role; ignore stale duplicates by updated_date. */
export const pickUserSavedRole = (roles = [], userId, roleName) => {
  const targetName = normalizeRoleName(roleName);
  const userRoles = roles.filter(
    (role) =>
      role.user_id === userId &&
      normalizeRoleName(role.role_name) === targetName &&
      extractRolePermissionIds(role).length > 0
  );

  if (!userRoles.length) return null;

  return [...userRoles].sort((a, b) => {
    const aTime = new Date(a.updated_date || a.created_date || 0).getTime();
    const bTime = new Date(b.updated_date || b.created_date || 0).getTime();
    return bTime - aTime;
  })[0];
};

export const fetchUserRoleByName = async (roleName, userId) => {
  const response = await axios.get(API_GET_ROLE_BY_NAME, {
    params: { name: roleName, user_id: userId },
  });

  const roles = response.data?.roles || [];
  return pickUserSavedRole(roles, userId, roleName);
};

export const fetchDefaultRoleByName = async (roleName) => {
  try {
    const response = await axios.get(API_GET_DEFAULT_ROLE_BY_NAME, {
      params: { name: roleName },
    });

    const roles = response.data?.roles || [];
    const targetName = normalizeRoleName(roleName);

    return (
      roles.find((role) => normalizeRoleName(role.role_name) === targetName) ||
      roles[0] ||
      null
    );
  } catch (error) {
    console.error("Error fetching default role by name:", error);
    return null;
  }
};

/**
 * Resolve permissions for a recommended role:
 * 1. User's saved role (getRoleByName + user_id)
 * 2. System default suggestion (getDefaultRoleByName)
 */
export const resolveRecommendedRolePermissions = async (roleName, userId) => {
  const savedRole = await fetchUserRoleByName(roleName, userId);
  if (savedRole) {
    return {
      source: "saved",
      description: savedRole.description || "",
      permissionIds: extractRolePermissionIds(savedRole),
    };
  }

  const defaultRole = await fetchDefaultRoleByName(roleName);
  if (defaultRole) {
    return {
      source: "default",
      description: defaultRole.description || "",
      permissionIds: extractRolePermissionIds(defaultRole),
    };
  }

  return null;
};

export const fetchDefaultRolePermissionCount = async (roleName) => {
  const defaultRole = await fetchDefaultRoleByName(roleName);
  return extractRolePermissionIds(defaultRole).length;
};
